import type { FlightSegment } from '@/types/pricing';

// Convert 'DD.MM.YYYY' to 'DDMONYY' (e.g., '15.06.2024' -> '15JUN24')
function formatDateForAmadeus(dateStr: string): string {
  if (!dateStr) return '';

  const [day, month, year] = dateStr.split(/[.\/-]/);
  const months = [
    'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
    'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC',
  ];

  const monthIndex = parseInt(month, 10) - 1;
  if (monthIndex < 0 || monthIndex > 11) return '';

  return `${day}${months[monthIndex]}${year.slice(-2)}`;
}

/**
 * Generate an Amadeus FQP (Fare Quote Pricing) command string from flight segments
 *
 * Format: FQP<origin>/A<airline>/C<class>/H<dep_time>/D<date>/H<arr_time><destination>...
 *
 * Example output: FQPFRA/ALH/CY/H1850/D15JUN24/H1940MUC/R,15JUN24
 */
export function generateFQPString(
  segments: FlightSegment[],
  ticketingDate: string
): string {
  if (!segments || segments.length === 0) return '';

  let fqp = 'FQP';

  // Start with first segment's origin
  fqp += segments[0].origin.toUpperCase();

  // Add each segment
  segments.forEach((seg) => {
    // Airline code
    if (seg.airline) {
      fqp += `/A${seg.airline.toUpperCase()}`;
    }

    // Booking class
    if (seg.bookingClass) {
      fqp += `/C${seg.bookingClass.toUpperCase()}`;
    }

    // Departure time (remove colon: 18:50 -> 1850)
    if (seg.departureTime) {
      fqp += `/H${seg.departureTime.replace(':', '')}`;
    }

    // Flight date
    if (seg.flightDate) {
      fqp += `/D${formatDateForAmadeus(seg.flightDate)}`;
    }

    // Arrival time
    if (seg.arrivalTime) {
      fqp += `/H${seg.arrivalTime.replace(':', '')}`;
    }

    // Destination
    if (seg.destination) {
      fqp += seg.destination.toUpperCase();
    }
  });

  // Add ticketing date restriction
  if (ticketingDate) {
    fqp += `/R,${formatDateForAmadeus(ticketingDate)}`;
  }

  return fqp;
}

/**
 * Validate a 3-letter IATA airport code
 */
export function isValidAirportCode(code: string): boolean {
  return /^[A-Z]{3}$/i.test(code);
}

/**
 * Validate a 2-letter IATA airline code
 */
export function isValidAirlineCode(code: string): boolean {
  return /^[A-Z0-9]{2}$/i.test(code);
}

/**
 * Validate a single-letter booking class
 */
export function isValidBookingClass(code: string): boolean {
  return /^[A-Z]$/i.test(code);
}

/**
 * Validate time format HH:MM
 */
export function isValidTime(time: string): boolean {
  return /^([01]?[0-9]|2[0-3]):[0-5][0-9]$/.test(time);
}

/**
 * Validate date format DD.MM.YYYY
 */
export function isValidDate(date: string): boolean {
  if (!/^\d{2}\.\d{2}\.\d{4}$/.test(date)) return false;

  const [day, month, year] = date.split('.').map(Number);
  const dateObj = new Date(year, month - 1, day);

  return (
    dateObj.getFullYear() === year &&
    dateObj.getMonth() === month - 1 &&
    dateObj.getDate() === day
  );
}
